import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';
import { OriginalMediaInfo } from '../../../_global/fragments/generated/OriginalMediaInfo';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type AssetDownloadInfo_AudioAsset_Fragment = { __typename?: 'AudioAsset', id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'AudioMedia', id: string, audioTranscodes?: Array<{ __typename?: 'AudioTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null } | null };

export type AssetDownloadInfo_DocumentAsset_Fragment = { __typename?: 'DocumentAsset', id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'DocumentMedia', id: string, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, metadata?: { __typename?: 'DocumentMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null, baseUrl?: string | null } | null, pdfDownload?: { __typename?: 'PdfDownloadInfo', url?: string | null } | null } | null };

export type AssetDownloadInfo_FileAsset_Fragment = { __typename?: 'FileAsset', id: string, name: string, isWatermarked: boolean };

export type AssetDownloadInfo_FolderAsset_Fragment = { __typename?: 'FolderAsset', id: string, name: string, isWatermarked: boolean };

export type AssetDownloadInfo_ImageAsset_Fragment = { __typename?: 'ImageAsset', id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'ImageMedia', id: string, imageTranscodes?: Array<{ __typename?: 'ImageTranscodeInfo', downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, key: string, streamUrl?: string | null }> | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, metadata?: { __typename?: 'ImageMetadata', id?: string | null, originalHeight?: number | null, originalWidth?: number | null } | null } | null };

export type AssetDownloadInfo_InteractiveAsset_Fragment = { __typename?: 'InteractiveAsset', id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'InteractiveMedia', id: string, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null } | null };

export type AssetDownloadInfo_InteractiveLiveAsset_Fragment = { __typename?: 'InteractiveLiveAsset', id: string, name: string, isWatermarked: boolean };

export type AssetDownloadInfo_ModelAsset_Fragment = { __typename?: 'ModelAsset', forensicallyWatermarked?: boolean | null, id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'ModelMedia', id: string, filesize?: number | null, metadata?: { __typename?: 'ModelMetadata', id?: string | null, originalWidth?: number | null, originalHeight?: number | null } | null, modelTranscodes?: Array<{ __typename?: 'ModelTranscodeInfo', codec?: string | null, downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, filesizeInBytes?: number | null, key: string, streamUrl?: string | null }> | null, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, videoTranscodes?: Array<{ __typename?: 'VideoTranscodeInfo', codec?: string | null, downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, filesizeInBytes?: number | null, height?: number | null, key: string, streamUrl?: string | null, width?: number | null }> | null } | null };

export type AssetDownloadInfo_UnsupportedAsset_Fragment = { __typename?: 'UnsupportedAsset', id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'UnsupportedMedia', id: string, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null } | null };

export type AssetDownloadInfo_VersionStackAsset_Fragment = { __typename?: 'VersionStackAsset', id: string, name: string, isWatermarked: boolean };

export type AssetDownloadInfo_VideoAsset_Fragment = { __typename?: 'VideoAsset', forensicallyWatermarked?: boolean | null, id: string, name: string, isWatermarked: boolean, media?: { __typename?: 'VideoMedia', id: string, original?: { __typename?: 'OriginalInfo', key: string, downloadUrl?: string | null, filesizeInBytes?: number | null, inlineUrl?: string | null, codec?: string | null } | null, videoTranscodes?: Array<{ __typename?: 'VideoTranscodeInfo', codec?: string | null, downloadUrl?: string | null, encodeStatus: Types.EncodeStatusEnum, filesizeInBytes?: number | null, height?: number | null, key: string, streamUrl?: string | null, width?: number | null }> | null, metadata?: { __typename?: 'VideoMetadata', id?: string | null, originalWidth?: number | null, originalHeight?: number | null } | null } | null };

export type AssetDownloadInfoFragment = AssetDownloadInfo_AudioAsset_Fragment | AssetDownloadInfo_DocumentAsset_Fragment | AssetDownloadInfo_FileAsset_Fragment | AssetDownloadInfo_FolderAsset_Fragment | AssetDownloadInfo_ImageAsset_Fragment | AssetDownloadInfo_InteractiveAsset_Fragment | AssetDownloadInfo_InteractiveLiveAsset_Fragment | AssetDownloadInfo_ModelAsset_Fragment | AssetDownloadInfo_UnsupportedAsset_Fragment | AssetDownloadInfo_VersionStackAsset_Fragment | AssetDownloadInfo_VideoAsset_Fragment;

export const AssetDownloadInfo = /*#__PURE__*/ gql`
    fragment AssetDownloadInfo on Asset {
  id
  name
  isWatermarked
  ... on AudioAsset {
    media {
      audioTranscodes {
        downloadUrl
        encodeStatus
        key
        streamUrl
      }
      id
      original {
        ...OriginalMediaInfo
      }
    }
  }
  ... on DocumentAsset {
    media {
      id
      original {
        ...OriginalMediaInfo
      }
      metadata {
        id
        originalHeight
        originalWidth
        baseUrl
      }
      pdfDownload {
        url
      }
    }
  }
  ... on ImageAsset {
    media {
      id
      imageTranscodes {
        downloadUrl
        encodeStatus
        key
        streamUrl
      }
      original {
        ...OriginalMediaInfo
      }
      metadata {
        id
        originalHeight
        originalWidth
      }
    }
  }
  ... on VideoAsset {
    forensicallyWatermarked
    media {
      id
      original {
        ...OriginalMediaInfo
      }
      videoTranscodes {
        codec
        downloadUrl
        encodeStatus
        filesizeInBytes
        height
        key
        streamUrl
        width
      }
      metadata {
        id
        originalWidth
        originalHeight
      }
    }
  }
  ... on ModelAsset {
    forensicallyWatermarked
    media {
      id
      filesize
      metadata {
        id
        originalWidth
        originalHeight
      }
      modelTranscodes {
        codec
        downloadUrl
        encodeStatus
        filesizeInBytes
        key
        streamUrl
      }
      original {
        ...OriginalMediaInfo
      }
      videoTranscodes {
        codec
        downloadUrl
        encodeStatus
        filesizeInBytes
        height
        key
        streamUrl
        width
      }
    }
  }
  ... on UnsupportedAsset {
    media {
      id
      original {
        ...OriginalMediaInfo
      }
    }
  }
  ... on InteractiveAsset {
    media {
      id
      original {
        ...OriginalMediaInfo
      }
    }
  }
}
    ${OriginalMediaInfo}` as unknown as DocumentNode<AssetDownloadInfoFragment, unknown>;

        

        
export const useAssetDownloadInfoFragment = (
  input: string | Omit<UseFragmentOptions<AssetDownloadInfoFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Asset' } };
    return input;
  })();

  return useFragment<AssetDownloadInfoFragment>({
    fragment: AssetDownloadInfo,
    fragmentName: 'AssetDownloadInfo',
    ...options,
  });
};
  
        